import React from 'react';
import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { Testable, Tourable } from './types';
import { Tooltip } from './Tooltip';
import { trackEvent } from '../analytics';
// CSS-based loader component
const ButtonLoader = () => (
  <div className="flex space-x-1 justify-center items-center">
    <div className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.3s]"></div>
    <div className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.15s]"></div>
    <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
  </div>
);

const buttonTypes = {
  outline: 'bg-white hover:bg-gray-200 text-blue',
  monotone: 'bg-white hover:bg-gray-200 text-black',
  primary: 'bg-blue hover:bg-blue-700 text-white border-transparent',
  success: 'bg-green-500 text-white',
  danger: 'bg-red-500 hover:bg-red-600 text-white',
};

const buttonSizes = {
  normal: 'px-4 py-3 space-x-2',
  small: 'px-2 py-1 space-x-1',
};

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof buttonTypes;
  size?: keyof typeof buttonSizes;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  tooltip?: React.ReactNode;
  analyticsProperties?: { name: string; properties?: Record<string, any> };
  iconOnly?: boolean;
}

export type ButtonProps = Tourable<Testable<Props>>;

export function Button({
  variant = 'primary',
  size = 'normal',
  fullWidth = true,
  rightIcon,
  leftIcon,
  children,
  className,
  loading,
  tooltip,
  analyticsProperties,
  iconOnly = false,
  ...buttonProps
}: ButtonProps) {
  const formContext = useFormContext();
  const showLoading =
    loading ||
    (buttonProps.type === 'submit' && formContext?.formState.isSubmitting);

  const disabled =
    buttonProps.disabled || showLoading || formContext?.formState.isSubmitting;

  const button = (
    <button
      type="button"
      {...buttonProps}
      style={!disabled ? buttonProps.style : undefined}
      disabled={disabled}
      className={classNames(
        'flex  transition-all items-center border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
        disabled
          ? 'bg-gray-200 text-gray-400 pointer-events-none'
          : buttonTypes[variant],
        leftIcon || rightIcon ? 'justify-between' : 'justify-center',
        buttonSizes[size],
        className,
        fullWidth && 'w-full',
      )}
      onClick={async (e) => {
        trackEvent(analyticsProperties?.name, analyticsProperties?.properties);
        await buttonProps.onClick?.(e);
      }}
    >
      {!!leftIcon && !showLoading && <div>{leftIcon}</div>}
      {showLoading ? (
        <div className="py-1.5 flex justify-center w-full">
          <ButtonLoader />
        </div>
      ) : (
        <>{!iconOnly && <div>{children}</div>}</>
      )}
      {!!rightIcon && !showLoading && <div>{rightIcon}</div>}
    </button>
  );

  return tooltip ? <Tooltip tooltip={tooltip}>{button}</Tooltip> : button;
}
